import { Box, Button, Card, CardBody, CardProps, Flex, Stack, Switch } from "@chakra-ui/react";
import { BorderedCard, Icons, ToolTip, Typography } from "@reconvert/react-ui-component";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Condition } from "../models";
import useUnsavedBar, { PreventActionType, PreventActionTypeObject } from "../hooks/useUnsavedBar";
import { selectAppState } from "../app/slice/appSlice";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { BOTTOM_SHEET_TYPES, openBottomSheet } from "../app/slice/mobileSlice";

export interface ConditionCardProps extends CardProps {
  mode?: "default" | "collapsed" | "expand";
  actions?: React.JSX.Element[];
  isConditionOn: boolean;
  toggleCondition?: (conditionId: string, oldValue: boolean, value: boolean) => void;
  title: string;
  description?: string;
  isDisabled?: boolean;
  isSelected?: boolean;
  conditionId?: string;
  condition?: Condition;
  dragHandleProps?: any;
  snapshot?: any;
  alwaysShowDragHandle?: boolean;
  fromMobileHeader?: boolean;
}
const ConditionCard: React.FC<ConditionCardProps> = props => {
  const { t } = useTranslation();
  const { preventAction } = useUnsavedBar();
  const dispatch = useAppDispatch();
  const {
    mode,
    actions,
    children,
    toggleCondition,
    title,
    description,
    isDisabled,
    isSelected,
    conditionId,
    condition,
    isConditionOn,
    dragHandleProps,
    snapshot,
    alwaysShowDragHandle,
    fromMobileHeader,
    ...rest
  } = props;

  const { isMobile } = useAppSelector(selectAppState);
  const [isExpanded, setIsExpanded] = useState<boolean | undefined>(undefined);
  const toggleExpanded = useCallback(() => setIsExpanded(prev => (prev === undefined ? true : !prev)), []);
  const isDefault = useMemo(() => condition?.isDefault || false, [condition]);
  const [showIcon, setShowIcon] = useState(false);
  const showExpanded = useMemo(() => {
    if (isDefault) {
      return false;
    }

    return isExpanded !== undefined ? isExpanded : isSelected || false;
  }, [isDefault, isExpanded, isSelected]);

  useEffect(() => {
    setIsExpanded(undefined);
  }, [isSelected]);

  useEffect(() => {
    if (condition?.rule && mode === "expand") {
      setIsExpanded(true);
    }
  }, [isSelected, condition?.rule, mode]);

  const getCardBody = useCallback(() => {
    if (isDefault) {
      return (
        <>
          <Flex alignItems="center" justifyContent="space-between">
            <Flex gap={2} alignItems="center">
              <Typography variant="subheading1-r">{t(title || "")}</Typography>
              <ToolTip
                trigger={isMobile ? "click" : "hover"}
                height={"100%"}
                alignItems="center"
                placement="top"
                label={t(
                  "Upsell.com displays the default condition when a customer doesn't meet any specific condition, allowing for maximized conversions on your store.",
                )}
                maxWidth={"285px"}
              >
                <span>
                  <Icons.Question height={"100%"} display={"flex"} alignItems={"center"} color="neutrals.900" />
                </span>
              </ToolTip>
            </Flex>
            {/* <Icons.Eye color="neutrals.600" /> */}
          </Flex>

          <Flex height={"min-content"} mt={3} alignItems="center" justifyContent="space-between">
            <Flex>
              <Typography variant="body">{t(description ?? "")}</Typography>
            </Flex>
          </Flex>
        </>
      );
    }

    return (
      <>
        <Flex alignItems="center" gap={2} justifyContent="space-between" width={"100%"}>
          <Flex gap={2}>
            {" "}
            <Typography
              variant="subheading2"
              fontWeight={500}
              sx={{
                wordBreak: "break-word",
                overflowWrap: "break-word",
                whiteSpace: "normal",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {t(title || "")}
            </Typography>
            <Flex alignItems="center" gap={2} onClick={e => e.stopPropagation()}>
              <Typography variant="small" color="neutrals.900">
                {isConditionOn ? t("On") : t("Off")}
              </Typography>
              <Switch
                size="sm"
                color="success.800"
                isChecked={isConditionOn}
                onChange={e => {
                  preventAction(() => {
                    toggleCondition && toggleCondition(conditionId!, isConditionOn, e.target.checked);
                  }, PreventActionTypeObject.INLINE_CHANGE as PreventActionType)
                }}
              />
            </Flex>
          </Flex>
          {!fromMobileHeader ? <Flex gap={2} justifyContent="flex-end" onClick={e => e.stopPropagation()}>
            {actions &&
              actions?.length > 0 &&
              actions.map(item => {
                return item;
              })}
            {!showExpanded ? (
              <Icons.FilledArrowUp color={"neutrals.600"} onClick={toggleExpanded} />
            ) : (
              <Icons.FilledArrowDown color={"neutrals.600"} onClick={toggleExpanded} />
            )}
          </Flex> : <Button variant={"link"} size={"sm"} color={"link.color"} onClick={(e) => {
            e.stopPropagation();
            dispatch(openBottomSheet({ type: BOTTOM_SHEET_TYPES.EDIT_CONDITION, props: { conditionId: conditionId } }));
          }}>{t("Edit")}</Button>}
        </Flex>
      </>
    );
  }, [
    isDefault,
    title,
    description,
    conditionId,
    isConditionOn,
    actions,
    showExpanded,
    toggleExpanded,
    toggleCondition,
    fromMobileHeader,
    preventAction,
    t,
  ]);

  return (
    <Box pl={isSelected ? 2 : 0} pr={isSelected ? 2 : isMobile ? 0 : 4}>
      <MobileConditionCard fromMobileHeader={fromMobileHeader} isSelected={isSelected} isConditionOn={isConditionOn} >
        <Box textAlign="left">
          <Card
            border={"none"}
            direction={{ base: "column", sm: "row" }}
            overflow="hidden"
            variant="outline"
            p={5}
            bg={!isConditionOn ? "neutral.white" : snapshot?.isDragging ? "grays.1" : undefined}
            color={!isConditionOn ? "disable.color" : "neutral.600"}
            size="md"
            cursor="pointer"
            _hover={{
              background: !isConditionOn ? "neutrals.200" : "grays.1",
            }}
            boxShadow={"0px 2px 8px 0px rgba(106, 115, 129, 0.12)"}
            onMouseEnter={() => {
              setShowIcon(true);
            }}
            onMouseLeave={() => {
              setShowIcon(false);
            }}
            {...rest}
          >
            <Stack w="100%">
              <CardBody padding={"16px 16px 16px 12px"}>
                <Flex gap="8px" flexDirection={isDefault ? "column" : "row"} width={"100%"}>
                  {(showIcon || alwaysShowDragHandle) && !isDefault && (
                    <Flex {...dragHandleProps}>
                      <Icons.DotsSixVertical cursor="grab" />
                    </Flex>
                  )}
                  {getCardBody()}
                </Flex>
                {<Box mt={children ? 3 : ""}>{showExpanded && children}</Box>}
              </CardBody>
            </Stack>
          </Card>
        </Box>
      </MobileConditionCard>
    </Box>
  );
};

interface MobileConditionCardProps {
  fromMobileHeader?: boolean;
  isSelected?: boolean;
  isConditionOn?: boolean;
  children: React.ReactNode;
}

const MobileConditionCard = ({ fromMobileHeader, isSelected, isConditionOn, children }: MobileConditionCardProps) => {
  if (fromMobileHeader) {
    return (
      <Box
        bg="white"
        borderRadius="12px"
        border="1px solid"
        borderColor="neutrals.300"
        mx="8px"
        mt="8px"
        overflow="hidden"
      >
        {children}
      </Box>
    );
  }

  return (
    <BorderedCard
      borderPosition="left"
      borderColor={!isSelected ? "#6B7280" : "#9A8318"}
      borderRadius="4"
      boxShadow={isSelected ? "0px 12px 20px 0px rgba(106, 115, 129, 0.22);" : "md"}
      bg={!isConditionOn ? "neutrals.200" : undefined}
      mb={4}
    >
      {children}
    </BorderedCard>
  );
};


export default ConditionCard;
